import React from 'react';
import { VideoTrack, type TrackReference } from '@livekit/components-react';

// function ConnectingDots() {
//   return (
//     <span aria-hidden="true" className="inline-flex items-center gap-1">
//       <span className="size-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
//       <span className="size-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
//       <span className="size-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
//     </span>
//   );
// }

function ConnectingDots() {
  return (
    <span aria-hidden="true" className="inline-flex items-center gap-1">
      <span className="size-1.5 rounded-full bg-current animate-bounce-high" style={{ animationDelay: '0ms' }} />
      <span className="size-1.5 rounded-full bg-current animate-bounce-high" style={{ animationDelay: '150ms' }} />
      <span className="size-1.5 rounded-full bg-current animate-bounce-high" style={{ animationDelay: '300ms' }} />
    </span>
  );
}

interface EmbedWelcomeViewProps {
  startButtonText: string;
  onStartCall: () => void;
  onEndCall: () => void;
  isSessionActive: boolean;
  agentVideoTrack?: TrackReference;
  isConnecting?: boolean;
  progressBar?: number;
  fadeOut?: boolean;

  // Warmup video gating (OPTIONAL — dev behavior unchanged if not passed)
  warmupMode?: boolean;
  warmupEnded?: boolean;
  onWarmupEnded?: () => void;
}

export const EmbedWelcomeView = ({
  startButtonText,
  onStartCall,
  onEndCall,
  ref,
  isSessionActive,
  agentVideoTrack,
  isConnecting = false,
  progressBar = 0,
  fadeOut = false,

  // defaults keep dev behavior identical
  warmupMode = false,
  warmupEnded = false,
  onWarmupEnded,

  ...divProps
}: React.ComponentProps<'div'> & EmbedWelcomeViewProps) => {
  const avatarSrc = '/my-avatar.gif';


  const showVideo = isSessionActive && Boolean(agentVideoTrack);

  // Warmup overlay (only active when warmupMode is true and warmup hasn't ended)
  const [warmupDelayReady, setWarmupDelayReady] = React.useState(false);

  React.useEffect(() => {
    if (warmupMode && !warmupEnded) {
      const t = setTimeout(() => setWarmupDelayReady(true), 500);
      return () => clearTimeout(t);
    }
    setWarmupDelayReady(false);
  }, [warmupMode, warmupEnded]);

  const showWarmupVideo = warmupMode && !warmupEnded && warmupDelayReady;

  const handleButtonClick = () => {
    if (isSessionActive) {
      onEndCall();
    } else {
      onStartCall();
    }
  };

  const AVATAR_INTERACTION_WINDOW_SECONDS = 4 * 60; // 4 minutes
  const SHOW_TIME_UP_WARNING_TIME_SECONDS = AVATAR_INTERACTION_WINDOW_SECONDS - 30;

  return (
    <div
      ref={ref}
      {...divProps}
      className="min-h-screen w-full flex flex-col items-center justify-center font-abc-repro font-normal text-sm text-gray-900 p-4 md:p-8"
    >
      <div className="flex flex-col items-center gap-4 p-4 md:p-6 rounded-xl w-full max-w-md">
        {/* Avatar Container with Button */}
        <div className="relative flex flex-col items-center">
          <div className="relative w-[280px] h-[280px] md:w-[400px] md:h-[400px] transition-all duration-300 rounded-full overflow-hidden border-4 border-gray-200 shadow-lg">
            {showVideo ? (
              <VideoTrack trackRef={agentVideoTrack} className="absolute inset-0 w-full h-full object-cover block" />
            ) : (
              <img 
                src={avatarSrc}
                alt="placeholder"
                className="absolute inset-0 w-full h-full object-cover block"
              />
            )}
{/* absolute inset-0 w-full h-full object-cover block scale-[1.2] */}
            {showWarmupVideo && (
              <video
                src="/warmup/Main.mp4"
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover block"
                onEnded={() => {
                  onWarmupEnded?.();
                }}
                onError={(e) => {
                  onWarmupEnded?.(); // fail-open so user isn't stuck
                }}
              />
            )}
          </div>
            <button
              onClick={(e) => {
                if (!isConnecting) {
                  handleButtonClick();
                }
                e.currentTarget.blur();
              }}
              disabled={isConnecting}
              className={`
                absolute -bottom-0 left-1/2 z-30 -translate-x-1/2
                flex items-center justify-center gap-3
                rounded-md border-0
                px-3 py-1.5
                text-base font-normal text-white
                whitespace-nowrap
                outline-none
                shadow-[0_5px_20px_rgba(0,0,0,0.4)]
                transition-all duration-100 ease-in-out
                cursor-pointer
                ${
                  isConnecting
                    ? 'bg-[#040E45] animate-pulse-glow cursor-wait'
                    : isSessionActive
                    ? 'bg-red-500 animate-pulse-glow'
                    : 'bg-[#040E45] animate-pulse-glow'
                }
              `}
            >
              {!isConnecting && !isSessionActive && (
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0"
                  aria-hidden="true"
                >
                  <rect width="36" height="36" rx="8" fill="#EBEDF8" />
                  <path
                    d="M13 24V12H15V24H13ZM17 28V8H19V28H17ZM9 20V16H11V20H9ZM21 24V12H23V24H21ZM25 20V16H27V20H25Z"
                    fill="#040E45"
                  />
                </svg>
              )}

              <span className={`${!isConnecting && !isSessionActive ? 'w-[164px]' : ''} text-center font-bold`}>
                {isConnecting
                  ? 'Connecting...'
                  : isSessionActive
                  ? 'Stop'
                  : 'Talk to Joy Live'}
              </span>
            </button>
        </div>

        {/* Description text below */}
        <div className="flex flex-col items-center">
          <p className="text-center text-base text-[#040E45] control-button-description max-w-[320px] md:max-w-[400px]">
            Learn how you can become a better negotiator
          </p>

          {/* Progress bar and time-up warning */}
          <style>{`
            .count-up-progress-bar {
              width: 280px;
              height: 8px;
              border-radius: 9999px;
              overflow: hidden;
              background-color: rgba(255,255,255,0.25);
            }
            @media (min-width: 768px) {
              .count-up-progress-bar { width: 400px; }
            }
            .count-up-progress-bar::-webkit-progress-bar {
              background-color: transparent;
            }
            .count-up-progress-bar::-webkit-progress-value {
              background-color: #22c55e; /* Tailwind green-500 */
              transition: width 0.2s ease;
            }
            .count-up-progress-bar::-moz-progress-bar {
              background-color: #22c55e;
              transition: width 0.2s ease;
            }
          `}</style>

          {progressBar > 0 ? (
            <progress className="count-up-progress-bar mt-3" max={AVATAR_INTERACTION_WINDOW_SECONDS} value={progressBar}></progress>
          ) : null}

          {progressBar > SHOW_TIME_UP_WARNING_TIME_SECONDS && (
            <p
              className={`text-center text-sm mt-2 text-gray-400 control-button-description w-[280px] md:w-[400px] transition-opacity duration-[3s] ${
                fadeOut ? 'opacity-0' : 'opacity-100'
              }`}
            >
              Wrapping up in 30 seconds...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
